import type { AxiosRequestConfig, CancelTokenSource } from 'axios';
import axios from 'axios';
import type { SnakeCasedPropertiesDeep } from 'type-fest';
import { keysToCamelCase, keysToSnakeCase } from '@hotjar/insights-common/lib/convert-string';
import { getApiHost } from '@hotjar/common';
import type { ResponseWithStatus } from './model';
import { mapResponseErrorToMessage } from './utils';

interface ApiRequest<TResponseData, TResponseDto, TRequestData, TRequestDto> {
  url: string;
  method?: AxiosRequestConfig['method'];
  params?: AxiosRequestConfig['params'];
  data?: TRequestData;
  host?: string;
  cancelTokenSource?: CancelTokenSource;
  mapResponseData?: (data: TResponseDto) => TResponseData;
  mapRequestData?: (data: TRequestData) => TRequestDto;
}

/**
 * A simple wrapper function that:
 *   - hides to client consumer what fetching mechanism (i.e. axios) is being used
 *   - allows for using only camelCase models if the dto is a one-to-one mapping (think backend for frontend payloads)
 *   - allows to override mapping functions for more specialised UI data
 */
export const client = async <
  TResponseData,
  TResponseDto = SnakeCasedPropertiesDeep<TResponseData>,
  TRequestData = unknown,
  TRequestDto = SnakeCasedPropertiesDeep<TRequestData>,
>({
  url,
  method = 'GET',
  data,
  host = getApiHost(),
  cancelTokenSource,
  mapResponseData = (responseData) =>
    keysToCamelCase(responseData, true) as unknown as TResponseData,
  mapRequestData = (requestData) => keysToSnakeCase(requestData, true) as unknown as TRequestDto,
}: ApiRequest<TResponseData, TResponseDto, TRequestData, TRequestDto>): Promise<TResponseData> => {
  try {
    const response = await axios.request<ResponseWithStatus<TResponseDto>>({
      url: `${host}${url}`,
      method,
      data: mapRequestData(data as TRequestData),
      cancelToken: cancelTokenSource?.token,
    });

    if (response.data?.success === false) {
      throw new Error(mapResponseErrorToMessage(response.data.error));
    }

    return mapResponseData(response.data);
  } catch (error) {
    throw new Error(mapResponseErrorToMessage(error as Error));
  }
};
